import { push } from 'react-router-redux';

import * as InvoiceModel from 'models/invoice';
import * as InvoiceRepository from 'repositories/invoice';

import { FlashMessageActionCreators } from 'actions/flashMessage';
import { LayoutActionCreators } from 'actions/layout';
import { ROUTE_INVOICE_DETAIL, ROUTE_INVOICE_ERROR, ROUTE_INVOICE_PAGE } from 'constants/app';
import { BALANCE_OVER_LIMIT, INVALID_REQUEST, INVOICE_CREATED_OVER_LIMIT, MONEY_IN_PER_MONTH_OVER_LIMIT } from 'constants/error';
import { InvoiceStatusFilter } from 'constants/invoice';
import { handleInvoiceApiError } from 'helper/error';
import { ActionCreator } from 'helper/redux';
import { Thunk } from 'models/redux';

export const InvoiceActionCreators = {
  appendInvoicePage: new ActionCreator<'invoice/AppendInvoicePage', InvoiceModel.InvoicePage>('invoice/AppendInvoicePage'),
  createInvoice: new ActionCreator<'invoice/CreateInvoice', InvoiceModel.Invoice>('invoice/CreateInvoice'),
  removeInvoice: new ActionCreator<'invoice/RemoveInvoice', InvoiceModel.Invoice>('invoice/RemoveInvoice'),
  setHasNotPassedOnboarding: new ActionCreator<'invoice/SetHasNotPassedOnboarding', void>('invoice/SetHasNotPassedOnboarding'),
  setHasPassedOnboarding: new ActionCreator<'invoice/SetHasPassedOnboarding', void>('invoice/SetHasPassedOnboarding'),
  setIsInvoicePageUpdated: new ActionCreator<'invoice/SetIsInvoicePageUpdated', void>('invoice/SetIsInvoicePageUpdated'),
  setIsOnRequest: new ActionCreator<'invoice/SetIsOnRequest', void>('invoice/SetIsOnRequest'),
  setIsReceived: new ActionCreator<'invoice/SetIsReceived', void>('invoice/SetIsReceived'),
  showInvoicePage: new ActionCreator<'invoice/ShowInvoicePage', InvoiceModel.InvoicePage>('invoice/ShowInvoicePage'),
  updateErrorType: new ActionCreator<'invoice/UpdateErrorType', string>('invoice/UpdateErrorType'),
  updateInvoice: new ActionCreator<'invoice/UpdateInvoice', InvoiceModel.Invoice>('invoice/UpdateInvoice')
};

export function loadMore(invoicePage: InvoiceModel.InvoicePage, statusFilter: InvoiceStatusFilter): Thunk {
  return (dispatch) => {
    dispatch(InvoiceActionCreators.setIsOnRequest.create());

    const nextPage = invoicePage.page.number + 1;
    return InvoiceRepository.query(nextPage, statusFilter)
      .then((updatedInvoicePage) => {
        dispatch(InvoiceActionCreators.appendInvoicePage.create(updatedInvoicePage));
        dispatch(InvoiceActionCreators.setIsReceived.create());
      })
      .catch((e) => {
        handleInvoiceApiError('action.failed.invoice.load', e, dispatch);
        dispatch(InvoiceActionCreators.setIsReceived.create());
        console.error(e); // tslint:disable-line
      });
  };
}

export function loadNew(statusFilter: InvoiceStatusFilter): Thunk {
  return (dispatch) => {
    dispatch(InvoiceActionCreators.setIsOnRequest.create());
    dispatch(LayoutActionCreators.showLoading.create());

    const firstPage = 1;
    return InvoiceRepository.query(firstPage, statusFilter)
      .then((updatedInvoicePage) => {
        dispatch(InvoiceActionCreators.showInvoicePage.create(updatedInvoicePage));
        dispatch(InvoiceActionCreators.setIsReceived.create());
        dispatch(InvoiceActionCreators.setIsInvoicePageUpdated.create());
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        handleInvoiceApiError('action.failed.invoice.query', e, dispatch);
        dispatch(InvoiceActionCreators.setIsReceived.create());
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}

export function update(invoice: InvoiceModel.Invoice): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());
    return InvoiceRepository.update(invoice)
      .then((updatedInvoice) => {
        dispatch(InvoiceActionCreators.updateInvoice.create(updatedInvoice));
        dispatch(push(ROUTE_INVOICE_DETAIL.replace(':id', updatedInvoice.id)));
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        if (e.body.error === BALANCE_OVER_LIMIT || e.body.error === MONEY_IN_PER_MONTH_OVER_LIMIT) {
          dispatch(InvoiceActionCreators.updateErrorType.create(e.body.error));
          dispatch(push(ROUTE_INVOICE_ERROR));
        } else {
          handleInvoiceApiError('action.failed.invoice.update', e, dispatch);
        }
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}

export function create(invoiceForm: InvoiceModel.InvoiceForm): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());
    return InvoiceRepository.create(invoiceForm)
      .then((invoice) => {
        dispatch(InvoiceActionCreators.createInvoice.create(invoice));
        dispatch(push(ROUTE_INVOICE_DETAIL.replace(':id', invoice.id)));
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        if (e.body.error === BALANCE_OVER_LIMIT || e.body.error === INVOICE_CREATED_OVER_LIMIT || e.body.error === MONEY_IN_PER_MONTH_OVER_LIMIT) {
          dispatch(InvoiceActionCreators.updateErrorType.create(e.body.error));
          dispatch(push(ROUTE_INVOICE_ERROR));
        } else {
          handleInvoiceApiError('action.failed.invoice.create', e, dispatch);
        }
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}

export function remove(invoice: InvoiceModel.Invoice): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());
    return InvoiceRepository.remove(invoice)
      .then(() => {
        dispatch(InvoiceActionCreators.removeInvoice.create(invoice));
        dispatch(push(ROUTE_INVOICE_PAGE));
        dispatch(FlashMessageActionCreators.show.create({
          modifier: 'success',
          text: 'action.success.invoice.delete'
        }));
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        handleInvoiceApiError('action.failed.invoice.remove', e, dispatch);
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}

export function getDetail(invoiceId: string): Thunk {
  return (dispatch) => {
    dispatch(LayoutActionCreators.showLoading.create());
    return InvoiceRepository.getDetail(invoiceId)
      .then((invoice) => {
        dispatch(InvoiceActionCreators.updateInvoice.create(invoice));
        dispatch(LayoutActionCreators.hideLoading.create());
      })
      .catch((e) => {
        dispatch(InvoiceActionCreators.updateErrorType.create(INVALID_REQUEST));
        dispatch(push(ROUTE_INVOICE_ERROR));
        dispatch(LayoutActionCreators.hideLoading.create());
        console.error(e); // tslint:disable-line
      });
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/invoice.ts